import { useEffect, useRef, useCallback } from 'react';
import { usePostHog } from '@posthog/react';
import type { WizardStep } from '../components/PnidWizardSidebar';

const IDLE_TIMEOUT_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 60_000;
const ACTIVITY_THROTTLE_MS = 1_000;

interface ZoneMs {
  canvas: number;
  form: number;
  sidebar: number;
  other: number;
}

interface StepTimerState {
  stepEnteredAt: number;
  lastActivityAt: number;
  activeMs: number;
  idleMs: number;
  idleCount: number;
  isIdle: boolean;
  idleStartedAt: number | null;
  zoneMs: ZoneMs;
  lastZone: keyof ZoneMs;
  lastZoneTs: number;
}

function createInitialState(): StepTimerState {
  const now = Date.now();
  return {
    stepEnteredAt: now,
    lastActivityAt: now,
    activeMs: 0,
    idleMs: 0,
    idleCount: 0,
    isIdle: false,
    idleStartedAt: null,
    zoneMs: { canvas: 0, form: 0, sidebar: 0, other: 0 },
    lastZone: 'other',
    lastZoneTs: now,
  };
}

function classifyZone(el: Element | null): keyof ZoneMs {
  if (!el) return 'other';
  if (el.closest('.konvajs-content') || el.tagName === 'CANVAS') return 'canvas';
  if (
    el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA'
    || el.closest('[data-zone="form"]')
  ) return 'form';
  if (el.closest('[data-zone="sidebar"]')) return 'sidebar';
  return 'other';
}

/**
 * Tracks time spent in each wizard step, splitting wall-clock time into
 * active vs idle buckets. Fires `step_entered`, `step_exited`, and
 * periodic `activity_heartbeat` events to PostHog.
 *
 * Idle = 30s of no mouse/keyboard/scroll activity OR tab hidden.
 */
export function useStepTimer(
  wizardStep: WizardStep,
  diagramId: string | null,
  stepLabels: Record<WizardStep, string>,
) {
  const posthog = usePostHog();
  const state = useRef<StepTimerState>(createInitialState());
  const prevStep = useRef<WizardStep>(wizardStep);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastThrottledActivity = useRef(0);
  const diagramRef = useRef(diagramId);
  const stepLabelsRef = useRef(stepLabels);
  const unmountGuard = useRef(false);

  diagramRef.current = diagramId;
  stepLabelsRef.current = stepLabels;

  const flushIdlePeriod = useCallback(() => {
    const s = state.current;
    if (s.isIdle && s.idleStartedAt !== null) {
      s.idleMs += Date.now() - s.idleStartedAt;
      s.idleStartedAt = null;
      s.isIdle = false;
    }
  }, []);

  const markIdle = useCallback(() => {
    const s = state.current;
    if (s.isIdle) return;
    const now = Date.now();
    s.activeMs += now - s.lastActivityAt;
    s.isIdle = true;
    s.idleStartedAt = now;
    s.idleCount += 1;
  }, []);

  const recordActivity = useCallback((eventTarget?: EventTarget | null) => {
    const now = Date.now();
    if (now - lastThrottledActivity.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottledActivity.current = now;

    const s = state.current;
    if (s.isIdle) {
      flushIdlePeriod();
    }

    // Zone tracking: accumulate time in previous zone while active
    if (!s.isIdle) {
      const elapsed = now - s.lastZoneTs;
      s.zoneMs[s.lastZone] += elapsed;
    }
    // Use actual event target (captures canvas interactions) with activeElement fallback
    const targetEl = eventTarget instanceof Element ? eventTarget : document.activeElement;
    s.lastZone = classifyZone(targetEl);
    s.lastZoneTs = now;

    s.lastActivityAt = now;
    s.isIdle = false;

    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(markIdle, IDLE_TIMEOUT_MS);
  }, [flushIdlePeriod, markIdle]);

  const buildStepProps = useCallback(
    (step: WizardStep) => ({
      step_number: step,
      step_name: stepLabelsRef.current[step],
      diagram_id: diagramRef.current,
    }),
    [],
  );

  const emitStepExited = useCallback(
    (exitedStep: WizardStep, nextStep: WizardStep | null) => {
      const s = state.current;
      const now = Date.now();

      if (s.isIdle && s.idleStartedAt !== null) {
        s.idleMs += now - s.idleStartedAt;
      } else {
        s.activeMs += now - s.lastActivityAt;
      }

      const durationMs = now - s.stepEnteredAt;

      posthog?.capture('step_exited', {
        ...buildStepProps(exitedStep),
        next_step: nextStep,
        next_step_name: nextStep ? stepLabelsRef.current[nextStep] : null,
        duration_ms: durationMs,
        active_duration_ms: s.activeMs,
        idle_duration_ms: s.idleMs,
        idle_count: s.idleCount,
        zone_canvas_ms: s.zoneMs.canvas,
        zone_form_ms: s.zoneMs.form,
        zone_sidebar_ms: s.zoneMs.sidebar,
        zone_other_ms: s.zoneMs.other,
      });
    },
    [posthog, buildStepProps],
  );

  // Activity listeners
  useEffect(() => {
    const onActivity = (e: Event) => recordActivity(e.target);
    const onVisibilityChange = () => {
      if (document.hidden) {
        markIdle();
      } else {
        recordActivity();
      }
    };

    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true, capture: true });
    window.addEventListener('click', onActivity, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    idleTimer.current = setTimeout(markIdle, IDLE_TIMEOUT_MS);

    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity, { capture: true } as EventListenerOptions);
      window.removeEventListener('click', onActivity);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [recordActivity, markIdle]);

  // Heartbeat
  useEffect(() => {
    heartbeatTimer.current = setInterval(() => {
      const s = state.current;
      const now = Date.now();
      const periodIdleSeconds = s.isIdle && s.idleStartedAt
        ? (now - s.idleStartedAt) / 1000
        : 0;

      posthog?.capture('activity_heartbeat', {
        ...buildStepProps(prevStep.current),
        is_active: !s.isIdle,
        idle_seconds_in_period: Math.round(periodIdleSeconds),
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
    };
  }, [posthog, buildStepProps]);

  // Step transitions
  useEffect(() => {
    const previousStep = prevStep.current;
    const isTransition = previousStep !== wizardStep;

    if (isTransition) {
      emitStepExited(previousStep, wizardStep);
      prevStep.current = wizardStep;
      state.current = createInitialState();

      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(markIdle, IDLE_TIMEOUT_MS);
    }

    posthog?.capture('step_entered', {
      ...buildStepProps(wizardStep),
      prev_step: isTransition ? previousStep : null,
    });
    posthog?.register({
      step_number: wizardStep,
      step_name: stepLabelsRef.current[wizardStep],
    });
    // Only fire on wizardStep change (initial mount + transitions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep]);

  // Flush on unmount (tab close / navigation)
  useEffect(() => {
    return () => {
      if (unmountGuard.current) return;
      unmountGuard.current = true;
      emitStepExited(prevStep.current, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reliable flush on tab/window close (beforeunload + pagehide for mobile Safari)
  useEffect(() => {
    const handleUnload = () => {
      if (unmountGuard.current) return;
      unmountGuard.current = true;
      emitStepExited(prevStep.current, null);
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [emitStepExited]);
}
